import {
    useState
} from "react";
import {
    View
} from "react-native";
import type {
    VerifyMailCodeProps
} from "./type";
import stylesheet from "./stylesheet";
import {
    Button,
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    Text
} from "ncore-ui-kit";
import {
    useDebouncedDimensions
} from "../../../../../utils";
import {
    CodeField,
    useBlurOnFulfill,
    useClearByFocusCell
} from "react-native-confirmation-code-field";
import type {
    GetMyProfilePayloadType
} from "../../../../../services/restAPI/actions/getMyProfile/types";
import {
    Check,
    CircleAlert
} from "lucide-react-native";
import {
    RESTService
} from "../../../../../services/restAPI";

const CELL_COUNT = 6;

const VerifyMailCode = ({
    setData,
    data,
    onClose
}: VerifyMailCodeProps) => {
    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        windowWidth
    } = useDebouncedDimensions();

    const cellSize = (windowWidth * 13) / 100;

    const [isError,
        setIsError] = useState(false);
    const [value,
        setValue] =useState("");
    const ref = useBlurOnFulfill({
        value,
        cellCount: CELL_COUNT
    });
    const [props,
        getCellOnLayoutHandler] = useClearByFocusCell({
        value,
        setValue
    });

    const handleCode = () => {
        if(value.length < 6) {
            NCoreUIKitSnackBar.open({
                title: localize("codeMinLength"),
                type: "danger",
                isShowAction: false,
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        RESTService.action("FinishMailVerify", {
            code: value
        }).then(() => {
            NCoreUIKitToast.open({
                title: localize("emailVerifiedSuccessfully"),
                type: "success",
                isShowAction: false,
                icon: ({
                    color
                }) => <Check
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            const _data:GetMyProfilePayloadType  = JSON.parse(JSON.stringify(data));
            _data.isMailVerified = true;
            setData(_data);
            onClose();
        }).catch((err) => {
            setIsError(true);
            console.log("err", err);
        });
    };

    return <View
        style={[
            stylesheet.container,
            {
                paddingHorizontal: spaces.spacingMd
            }
        ]}
    >
        <View>
            <CodeField
                ref={ref}
                {...props}
                textContentType="oneTimeCode"
                keyboardType="number-pad"
                onChangeText={(e) => {
                    setValue(e);
                }}
                cellCount={CELL_COUNT}
                autoCapitalize="none"
                value={value}
                rootStyle={[
                    stylesheet.codeFieldRoot
                ]}
                renderCell={({
                    isFocused,
                    symbol,
                    index
                }) => <View
                    key={index}
                    style={[
                        {
                            ...stylesheet.cell,
                            height: cellSize,
                            width: cellSize,
                            marginRight: index === 5 ? 0 : spaces.spacingXs,
                            marginLeft: index === 0 ? 0 : spaces.spacingXs,
                            backgroundColor: colors.content.container.default,
                            marginBottom: spaces.spacingMd,
                            borderRadius: radiuses.md,
                            borderWidth: borders.line,
                            borderColor: colors.content.border.default
                        },
                        isError && {
                            borderColor: colors.content.border.danger
                        },
                        isFocused && {
                            borderColor: colors.content.border.emphasized
                        }
                    ]}
                    onLayout={getCellOnLayoutHandler(index)}
                >
                    <Text
                        variant="titleMediumSize"
                        color="high"
                    >
                        {symbol || (isFocused ?  <Text
                            variant="titleLargeSize"
                        >
                            |
                        </Text> : null)}
                    </Text>
                </View>
                }
            />
            <Text
                color="mid"
                variant="bodyMediumSize"
                style={[
                    stylesheet.description,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                {localize("enterVerificationCode")}
            </Text>
        </View>
        <View
            style={stylesheet.buttonsContainer}
        >
            <Button
                onPress={() => {
                    handleCode();
                }}
                isDisabled={value.length < 6 ? true : false}
                customTextColor={colors.project.brandWhite}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                title={localize("verify")}
                customBorderColor="black"
                spreadBehaviour="stretch"
                customColor="black"
            />
            <Button
                customBorderColor={colors.project.black}
                title={localize("abandon")}
                spreadBehaviour="stretch"
                variant="outline"
                onPress={onClose}
            />
        </View>
    </View>;
};
export default VerifyMailCode;
